import { useTranslation } from 'react-i18next';
import { Pressable } from '@fluent/ui';
import { CheckGlyph, ReviewGlyph, StarGlyph } from './glyphs.js';
import type { PhraseLessonView } from './progression.js';
import {
  cefrAttr,
  isDone,
  ProBadge,
  showsPro,
  StartPlate,
  tileAttrs,
  useTileLabel,
  type LookProps,
} from './tile.js';

/**
 * The ფრაზები lesson list — „სტიკერების ალბომი“ (sticker album; Tornike
 * picked it 2026-10-18, option 3 of .cache/phrases/build/phrases-looks.html).
 * Each level is an album page in its colour; every lesson is a die-cut
 * sticker spot, three per row. The lesson NUMBER sits on the sticker and
 * the phrase under it, so a long phrase never squeezes into the circle.
 * Finished = a stuck-on sticker (slightly tilted, with a gold tick); not
 * yet = an empty dashed spot; review lessons are rounded squares.
 */

/** Same hand-placed feel as the skill tree's stickers. */
const TILTS = ['-4deg', '3deg', '-2deg', '5deg', '-3deg', '2deg'] as const;

export function AlbumLook({ levels, onOpen }: LookProps) {
  const { t } = useTranslation();
  const label = useTileLabel();
  return (
    <div className="fluent-phrases__album">
      {levels.map((level) => (
        <section
          key={level.unitId}
          className="fluent-phrases__page"
          data-cefr={cefrAttr(level)}
          data-phrases-level={level.code}
          aria-labelledby={`phrases-level-${level.code}`}
        >
          <div className="fluent-phrases__level-head fluent-phrases__page-head">
            <h2 className="fluent-phrases__level-name" id={`phrases-level-${level.code}`}>
              <span className="fluent-phrases__code">{level.code}</span>
              {level.title}
            </h2>
            <span className="fluent-phrases__level-count">
              {t('phrases.level_progress', { done: level.done, total: level.total })}
            </span>
          </div>
          <div className="fluent-phrases__grid">
            {level.lessons.map((lesson) => (
              <Pressable key={lesson.id} className="fluent-phrases__slot" {...tileAttrs(lesson, label(lesson), onOpen)}>
                <Sticker lesson={lesson} />
              </Pressable>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

function Sticker({ lesson }: { lesson: PhraseLessonView }) {
  const done = isDone(lesson);
  // A teaching lesson shows its phrase once it is reachable; a locked one
  // shows only its Georgian title (the phrase is the surprise). A review
  // shows its title either way.
  const caption = lesson.state === 'locked' || lesson.isReview ? null : (lesson.tileEn ?? lesson.title);
  return (
    <>
      <span
        className="fluent-phrases__sticker"
        style={done ? { ['--tilt' as string]: TILTS[lesson.n % TILTS.length] } : undefined}
      >
        {lesson.isReview ? <ReviewGlyph /> : <span className="fluent-phrases__sticker-n">{lesson.n}</span>}
        {done && (
          <span className="fluent-phrases__ok">{lesson.state === 'golden' ? <StarGlyph /> : <CheckGlyph />}</span>
        )}
        {showsPro(lesson) && <ProBadge />}
      </span>
      {caption ? (
        <span className="fluent-phrases__slot-p" lang={lesson.tileEn ? 'en' : undefined}>
          {caption}
        </span>
      ) : (
        <span className="fluent-phrases__slot-t">{lesson.title}</span>
      )}
      {lesson.state === 'next' && <StartPlate className="fluent-phrases__go" />}
    </>
  );
}
